/**
 * AvailabilityWindowFormFields — shared date-from/date-to/state-select fields
 * for the two "Zablokuj dostępność" dialogs:
 * - features/reservations/components/maintenance-block-dialog.tsx (single-unit)
 * - features/media-units/components/bulk-mark-availability-action.tsx (bulk)
 *
 * Cross-feature imports are forbidden between features/reservations and
 * features/media-units (ESLint import-x/no-restricted-paths), so this shared
 * presentational component and its validity predicate live in src/components/
 * — the established location for logic/components used by 2+ features (see
 * docs/WEB_ARCHITECTURE.md, "Shared logic used by 2+ features belongs in
 * src/lib/ or src/hooks/", extended here to a shared presentational component
 * alongside src/components/error-fallback.tsx and src/components/map-legend.tsx).
 *
 * Props-only component. No data fetching, no mutation call, no i18next import
 * — callers pass already-translated labels.
 */
import type { AvailabilityWindowState } from '@ooh/contracts';
import { AvailabilityWindowStateSchema } from '@ooh/contracts';
import { Label } from '@ooh/ui/components/label';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';
import { getDateFnsLocale } from '@ooh/ui/lib/date-format';

export interface AvailabilityWindowFormFieldsLabels {
  /** Single combined label shown above the date-range trigger (one field now, not two). */
  dateRangeLabel: string;
  dateRangePlaceholder: string;
  stateLabel: string;
  freeOptionLabel: string;
  unavailableOptionLabel: string;
  statePlaceholder: string;
}

export interface AvailabilityWindowFormFieldsTestIds {
  /** Testid of the single DateRangeButtonField trigger (also carries data-from-value/data-to-value). */
  dateRange: string;
  stateSelect: string;
}

export interface AvailabilityWindowFormFieldsProps {
  labels: AvailabilityWindowFormFieldsLabels;
  dateFrom: string;
  dateTo: string;
  state: AvailabilityWindowState | '';
  /** DateRangeButtonField reports both ends of the range together — see its onChange contract. */
  onRangeChange: (next: { from: string; to: string }) => void;
  onStateChange: (value: AvailabilityWindowState) => void;
  testIds: AvailabilityWindowFormFieldsTestIds;
  /** Optional className applied to the DateRangeButtonField wrapper (callers may match their own dialog's type scale). */
  className?: string;
}

/**
 * Validity predicate for the date-from/date-to/state form: both dates must be
 * set, dateTo must not precede dateFrom, and state must be a recognized
 * AvailabilityWindowState value.
 */
export function isAvailabilityWindowFormValid(
  dateFrom: string,
  dateTo: string,
  state: string,
): boolean {
  return (
    dateFrom !== '' &&
    dateTo !== '' &&
    dateTo >= dateFrom &&
    AvailabilityWindowStateSchema.safeParse(state).success
  );
}

export function AvailabilityWindowFormFields({
  labels,
  dateFrom,
  dateTo,
  state,
  onRangeChange,
  onStateChange,
  testIds,
  className,
}: AvailabilityWindowFormFieldsProps) {
  const stateOptions: { value: AvailabilityWindowState; label: string }[] =
    AvailabilityWindowStateSchema.options.map((value) => ({
      value,
      label: value === 'FREE' ? labels.freeOptionLabel : labels.unavailableOptionLabel,
    }));

  function handleStateChange(value: string) {
    const parsed = AvailabilityWindowStateSchema.safeParse(value);
    if (parsed.success) {
      onStateChange(parsed.data);
    }
  }

  return (
    <>
      <DateRangeButtonField
        fromValue={dateFrom}
        toValue={dateTo}
        onChange={onRangeChange}
        label={labels.dateRangeLabel}
        placeholder={labels.dateRangePlaceholder}
        locale={getDateFnsLocale()}
        testId={testIds.dateRange}
        className={className}
      />
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">{labels.stateLabel}</Label>
        <SearchableSelectField
          data-testid={testIds.stateSelect}
          options={stateOptions}
          value={state}
          onChange={handleStateChange}
          placeholder={labels.statePlaceholder}
        />
      </div>
    </>
  );
}
